import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Checkbox,
  Group,
  NativeSelect,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { ActionButton, AppModal } from '@/components/ui'
import { ShiftPresenceDot } from '@/components/ui/ShiftPresenceDot'
import { api, ApiError, DEFAULT_ERROR_NOTIFY_MESSAGE } from '@/lib/api'
import type { AIExceptionRow, AIExceptionWritePayload, ProjectTagRow, WorkspaceUserRow } from '@/types/api'

type Props = {
  projectId: string
  canEdit: boolean
  refreshKey: number
}

function userRole(u: WorkspaceUserRow): string {
  const r = u.role
  return typeof r === 'string' ? r : ''
}

function userLabel(u: WorkspaceUserRow): string {
  const name = `${u.first_name || ''} ${u.last_name || ''}`.trim()
  return name || u.email || u.id
}

function snippet(s: string | null | undefined, n: number) {
  const t = (s || '').trim()
  if (t.length <= n) return t
  return `${t.slice(0, n)}…`
}

const ACTION_LABEL: Record<string, string> = {
  skip: 'Пропустить',
  assign: 'Назначить',
  admin: 'Администратору',
}

export function AiExceptionsTab({ projectId, canEdit, refreshKey }: Props) {
  const [rows, setRows] = useState<AIExceptionRow[]>([])
  const [users, setUsers] = useState<WorkspaceUserRow[]>([])
  const [tags, setTags] = useState<ProjectTagRow[]>([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState<AIExceptionRow | 'new' | null>(null)
  const [saving, setSaving] = useState(false)

  const [name, setName] = useState('')
  const [conditions, setConditions] = useState('')
  const [actionType, setActionType] = useState('skip')
  const [assignedToId, setAssignedToId] = useState<string>('')
  const [adminToId, setAdminToId] = useState<string>('')
  const [tagId, setTagId] = useState<string>('')
  const [priority, setPriority] = useState<string>('')
  const [status, setStatus] = useState<string>('')
  const [internalNote, setInternalNote] = useState('')
  const [messageToClient, setMessageToClient] = useState('')
  const [active, setActive] = useState(true)

  const operators = useMemo(
    () =>
      users.filter((u) => {
        const role = userRole(u).toLowerCase()
        return (
          ['operator', 'trainee', 'shift_lead', 'npc_helper', 'human_helper', 'analyst'].includes(role) &&
          u.is_active !== false
        )
      }),
    [users],
  )

  const admins = useMemo(
    () =>
      users.filter((u) => {
        const role = userRole(u).toLowerCase()
        return (role === 'admin' || role === 'owner') && u.is_active !== false
      }),
    [users],
  )

  const operatorSelectData = useMemo(
    () =>
      operators.map((u) => ({
        value: u.id,
        label: `${userLabel(u)} (${u.email || ''})`,
      })),
    [operators],
  )

  const adminSelectData = useMemo(
    () =>
      admins.map((u) => ({
        value: u.id,
        label: `${userLabel(u)} (${u.email || ''})`,
      })),
    [admins],
  )

  const renderUserPickOption = useCallback(
    ({ option }: { option: { value: string; label: string } }) => (
      <Group gap={6} wrap="nowrap" align="center" py={2} w="100%">
        <ShiftPresenceDot userId={option.value} size={7} />
        <Text size="xs" truncate style={{ flex: 1, minWidth: 0 }}>
          {option.label}
        </Text>
      </Group>
    ),
    [],
  )

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [ex, u, t] = await Promise.all([
        api.aiExceptionsList(projectId),
        api.workspaceUsers(),
        api.projectTags(projectId),
      ])
      setRows(Array.isArray(ex) ? ex : [])
      setUsers(Array.isArray(u) ? u : [])
      setTags(Array.isArray(t) ? t : [])
    } catch {
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    void load()
  }, [load, refreshKey])

  const openNew = () => {
    setModal('new')
    setName('')
    setConditions('')
    setActionType('skip')
    setAssignedToId('')
    setAdminToId('')
    setTagId('')
    setPriority('')
    setStatus('')
    setInternalNote('')
    setMessageToClient('')
    setActive(true)
  }

  const openEdit = (row: AIExceptionRow) => {
    setModal(row)
    setName(row.name || '')
    setConditions(row.conditions || '')
    const at = row.action_type || 'skip'
    setActionType(at)
    if (at === 'assign') {
      setAssignedToId(row.assigned_to_id || '')
      setAdminToId('')
    } else if (at === 'admin') {
      setAssignedToId('')
      setAdminToId(row.assigned_to_id || '')
    } else {
      setAssignedToId('')
      setAdminToId('')
    }
    setTagId(row.tag_id || '')
    setPriority(row.priority || '')
    setStatus(row.status || '')
    setInternalNote(row.internal_note || '')
    setMessageToClient(row.message_to_client || '')
    setActive(row.is_active !== false)
  }

  const saveException = async () => {
    const n = name.trim()
    const c = conditions.trim()
    if (!n || !c) {
      showNotification({ title: 'Форма', message: 'Название и условия обязательны', color: 'yellow' })
      return
    }
    let assigned: string | null = null
    if (actionType === 'assign') assigned = assignedToId || null
    if (actionType === 'admin') assigned = adminToId || null

    const body: AIExceptionWritePayload = {
      name: n,
      conditions: c,
      action_type: actionType,
      assigned_to_id: assigned,
      tag_id: tagId || null,
      priority: priority || null,
      status: status || null,
      internal_note: internalNote.trim() || null,
      message_to_client: messageToClient.trim() || null,
      is_active: active,
    }

    setSaving(true)
    try {
      if (modal === 'new') {
        await api.aiExceptionCreate(projectId, body)
      } else if (modal) {
        await api.aiExceptionUpdate(projectId, modal.id, body)
      }
      showNotification({ title: 'Сохранено', message: '', color: 'green' })
      setModal(null)
      await load()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось сохранить',
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Stack gap="md">
      {canEdit ? (
        <Button size="sm" onClick={openNew}>
          Добавить исключение
        </Button>
      ) : null}

      {loading ? (
        <Text size="sm">Загрузка…</Text>
      ) : rows.length === 0 ? (
        <Text size="sm" c="dimmed">
          Исключений нет
        </Text>
      ) : (
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Название</Table.Th>
              <Table.Th>Условия</Table.Th>
              <Table.Th>Действие</Table.Th>
              <Table.Th>Назначено</Table.Th>
              <Table.Th>Тег</Table.Th>
              <Table.Th>Приоритет</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th>Акт.</Table.Th>
              <Table.Th w={180} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((r) => (
              <Table.Tr key={r.id}>
                <Table.Td>{r.name}</Table.Td>
                <Table.Td>{snippet(r.conditions, 50)}</Table.Td>
                <Table.Td>{ACTION_LABEL[r.action_type] || r.action_type}</Table.Td>
                <Table.Td>{r.assigned_to?.email || r.assigned_to_id || '—'}</Table.Td>
                <Table.Td>{r.tag?.name || '—'}</Table.Td>
                <Table.Td>{r.priority || '—'}</Table.Td>
                <Table.Td>{r.status || '—'}</Table.Td>
                <Table.Td>{r.is_active !== false ? '✓' : '✗'}</Table.Td>
                <Table.Td>
                  <Group gap={6}>
                    <Button size="compact-xs" variant="default" onClick={() => openEdit(r)}>
                      Изменить
                    </Button>
                    {canEdit ? (
                      <Button
                        size="compact-xs"
                        color="red"
                        variant="light"
                        onClick={() =>
                          openConfirmModal({
                            title: 'Удалить исключение?',
                            onConfirm: () =>
                              void api
                                .aiExceptionDelete(projectId, r.id)
                                .then(() => load())
                                .then(() => showNotification({ title: 'Удалено', message: '', color: 'green' }))
                                .catch((e) =>
                                  showNotification({
                                    title: 'Ошибка',
                                    message: e instanceof ApiError ? e.message : DEFAULT_ERROR_NOTIFY_MESSAGE,
                                    color: 'red',
                                  }),
                                ),
                          })
                        }
                      >
                        Удалить
                      </Button>
                    ) : null}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <AppModal
        opened={modal != null}
        onClose={() => setModal(null)}
        title="Исключение"
        size="lg"
        footer={
          <>
            <ActionButton action="cancel" onClick={() => setModal(null)} />
            <ActionButton action="save" loading={saving} onClick={() => void saveException()} />
          </>
        }
      >
        <Stack gap="sm">
          <TextInput label="Название" required value={name} onChange={(e) => setName(e.target.value)} />
          <Textarea
            label="Условия (ключевые слова через запятую)"
            required
            value={conditions}
            onChange={(e) => setConditions(e.target.value)}
            minRows={3}
            placeholder="коммерческое предложение, реклама"
          />
          <NativeSelect
            label="Действие"
            data={[
              { value: 'skip', label: 'Пропустить (не назначать никому)' },
              { value: 'assign', label: 'Назначить на человека' },
              { value: 'admin', label: 'Назначить администратору' },
            ]}
            value={actionType}
            onChange={(e) => setActionType(e.currentTarget.value)}
          />
          {actionType === 'assign' ? (
            <Select
              label="Оператор"
              searchable
              clearable
              disabled={!operators.length}
              placeholder={operators.length ? 'Выберите' : 'Нет доступных операторов'}
              data={operatorSelectData}
              value={assignedToId || null}
              onChange={(v) => setAssignedToId(v ?? '')}
              renderOption={renderUserPickOption}
              leftSection={
                assignedToId ? <ShiftPresenceDot userId={assignedToId} size={7} /> : undefined
              }
            />
          ) : null}
          {actionType === 'admin' ? (
            <Select
              label="Администратор"
              searchable
              clearable
              disabled={!admins.length}
              placeholder={admins.length ? 'Выберите' : 'Нет доступных администраторов'}
              data={adminSelectData}
              value={adminToId || null}
              onChange={(v) => setAdminToId(v ?? '')}
              renderOption={renderUserPickOption}
              leftSection={adminToId ? <ShiftPresenceDot userId={adminToId} size={7} /> : undefined}
            />
          ) : null}
          <NativeSelect
            label="Проставить тег"
            data={[{ value: '', label: 'Не проставлять' }, ...tags.map((t) => ({ value: t.id, label: t.name }))]}
            value={tagId}
            onChange={(e) => setTagId(e.currentTarget.value)}
          />
          <NativeSelect
            label="Приоритет"
            data={[
              { value: '', label: 'Не устанавливать' },
              { value: 'low', label: 'Низкий' },
              { value: 'normal', label: 'Обычный' },
              { value: 'high', label: 'Высокий' },
              { value: 'urgent', label: 'Срочный' },
            ]}
            value={priority}
            onChange={(e) => setPriority(e.currentTarget.value)}
          />
          <NativeSelect
            label="Статус"
            data={[
              { value: '', label: 'Не устанавливать' },
              { value: 'new', label: 'Новый' },
              { value: 'open', label: 'Открыт' },
              { value: 'in_progress', label: 'В работе' },
              { value: 'pending', label: 'Ожидает' },
              { value: 'resolved', label: 'Решено' },
              { value: 'closed', label: 'Закрыт' },
            ]}
            value={status}
            onChange={(e) => setStatus(e.currentTarget.value)}
          />
          <Textarea
            label="Внутренняя заметка"
            value={internalNote}
            onChange={(e) => setInternalNote(e.target.value)}
            minRows={2}
          />
          <Textarea
            label="Сообщение клиенту"
            value={messageToClient}
            onChange={(e) => setMessageToClient(e.target.value)}
            minRows={2}
          />
          <Checkbox label="Активно" checked={active} onChange={(e) => setActive(e.currentTarget.checked)} />
        </Stack>
      </AppModal>
    </Stack>
  )
}
